import { findFrame, FRAMES, isVipActive, type StylingDto } from '@spin/shared';
import { useEffect, useState } from 'react';
import { http } from '../api/http';
import { Dialog } from '../components/Dialog';
import { Avatar, Spinner } from '../components/common';
import { Emoji } from '../components/Emoji';
import { FrameArt, frameInk, hasFrameArt } from '../components/FrameArt';
import { PriceHeart } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { tg } from '../tg/bridge';

/** A square photo in a frame, as the table shows it. */
function FramedCard({
  name,
  photoUrl,
  frame,
}: {
  name: string;
  photoUrl: string | null;
  frame: string | null;
}) {
  const f = findFrame(frame);
  const ornate = hasFrameArt(f?.code);
  return (
    <span className="styling-card">
      <span
        className={f ? (ornate ? 'styling-photo framed ornate' : 'styling-photo framed') : 'styling-photo'}
        style={f && !ornate ? { background: f.ring, boxShadow: `0 0 12px ${f.glow}` } : undefined}
      >
        <Avatar name={name} photoUrl={photoUrl} size="100%" />
        {ornate && f && <FrameArt code={f.code} />}
        {f?.top && (
          <span className="frame-top">
            <Emoji e={f.top} />
          </span>
        )}
      </span>
      <span className="styling-name ellipsis">
        {f?.nameIcon}
        {name}
        {f?.nameIcon}
      </span>
    </span>
  );
}

/**
 * Styling: the frame picked last is previewed on my own card with its name and "Apply"; the grid shows every frame
 * with its price or a lock. Buying and wearing are one "Apply" tap after picking.
 */
export function StylingDialog() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const [data, setData] = useState<StylingDto | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const vip = isVipActive(me.vipUntil);

  useEffect(() => {
    http
      .get<StylingDto>('/styling')
      .then((d) => {
        setData(d);
        setPicked(d.equipped);
      })
      .catch(reportError);
  }, []);

  const call = async (path: string, body: object) => {
    const res = await http.post<StylingDto & { balance?: number }>(path, body);
    setData(res);
    useSession.getState().setMe({
      ...useSession.getState().me!,
      frame: res.equipped,
      ...(res.balance !== undefined ? { balance: res.balance } : {}),
    });
    return res;
  };

  const item = data?.frames.find((f) => f.code === picked) ?? null;
  const def = findFrame(picked);
  const unchanged = data ? picked === data.equipped : true;
  const locked = Boolean(item && !item.owned && (item.price === null || (item.vipOnly && !vip)));

  const apply = async () => {
    setBusy(true);
    try {
      if (picked && item && !item.owned) await call('/styling/buy', { code: picked });
      await call('/styling/equip', { code: picked });
      tg.haptic('success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  let hint = t('styling.none_hint');
  if (picked && item) {
    if (item.owned) hint = t('styling.owned_hint');
    else if (item.price === null)
      hint = def?.reward ? t(`styling.reward.${def.reward}`) : t('styling.locked');
    else if (item.vipOnly && !vip) hint = t('styling.vip_only');
    else hint = t('styling.buy_hint', { price: item.price });
  }

  return (
    <Dialog
      className="styling-dialog"
      title={picked ? t(`frame.${picked}`) : t('styling.none')}
      testId="styling"
    >
      {!data ? (
        <Spinner />
      ) : (
        <>
          <div className="styling-preview">
            <FramedCard name={me.displayName} photoUrl={me.photoUrl} frame={picked} />
            <div className="styling-side">
              <p>{hint}</p>
              <button
                className={unchanged || locked ? 'btn' : 'btn btn-green'}
                disabled={busy || unchanged || locked}
                onClick={() => void apply()}
                data-testid="apply-frame"
              >
                {item && !item.owned && item.price !== null && !locked ? (
                  <>
                    {t('styling.apply')} · <PriceHeart /> {item.price}
                  </>
                ) : (
                  t('styling.apply')
                )}
              </button>
            </div>
          </div>
          <div className="frame-grid">
            <button
              className={picked === null ? 'frame-cell picked' : 'frame-cell'}
              onClick={() => setPicked(null)}
              aria-label={t('styling.none')}
            >
              <span className="frame-swatch none">✕</span>
              {data.equipped === null && <span className="frame-check">✓</span>}
            </button>
            {FRAMES.map((f) => {
              const state = data.frames.find((x) => x.code === f.code);
              const lockedCell = !state?.owned && (f.price === null || (f.vipOnly && !vip));
              return (
                <button
                  key={f.code}
                  className={picked === f.code ? 'frame-cell picked' : 'frame-cell'}
                  onClick={() => setPicked(f.code)}
                  title={t(`frame.${f.code}`)}
                  data-testid={`frame-${f.code}`}
                >
                  {hasFrameArt(f.code) ? (
                    <span className="frame-swatch ornate">
                      <FrameArt code={f.code} />
                      <i style={{ color: frameInk(f.code) }}>
                        {f.top || f.corner ? <Emoji e={(f.top ?? f.corner)!} /> : f.nameIcon}
                      </i>
                    </span>
                  ) : (
                    <span className="frame-swatch" style={{ background: f.ring }}>
                      <i>{f.top ?? f.corner ?? f.nameIcon ?? ''}</i>
                    </span>
                  )}
                  {state?.equipped ? (
                    <span className="frame-check">✓</span>
                  ) : state?.owned ? (
                    <span className="price">{t('styling.owned')}</span>
                  ) : lockedCell ? (
                    <span className="price lock">🔒</span>
                  ) : (
                    <span className="price">
                      <PriceHeart />
                      {f.price}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </Dialog>
  );
}
